import React, { useEffect, useRef, useState, useContext } from "react";
import { CaretDownFill, CaretUpFill } from "../Icons/CaretIcon";
import { message } from "antd";
import MagicWand from "../Icons/MagicWand";
import { useHistory } from "react-router-dom";
import FiltersContext from "../../contexts/Filters";
import * as Md from "../../md/full";
import { newPositiveAttributeFilter } from "@gooddata/sdk-model";
import { useAppContext } from "../../contexts/AppContext";
const CondensedTable = ({ columns, data, onSort, stickyColumn }) => {
    const contentRef = useRef();
    const scrollRef = useRef();
    const [contentWidth, setContentWidth] = useState(0);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
    const history = useHistory();
    const { clearFilters, updateFilter } = useContext(FiltersContext);
    const { setMatchVehicle } = useAppContext();

    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 100;
    const totalPages = Math.ceil(data.length / rowsPerPage);

    // Determine min and max for Leasing_Factor
    const leasingFactorValues = data.map((row) => parseFloat(row["Leasing_Factor"])).filter((val) => !isNaN(val));
    const minLeasingFactor = Math.min(...leasingFactorValues);
    const maxLeasingFactor = Math.max(...leasingFactorValues);
    const range = maxLeasingFactor - minLeasingFactor;

    const getCurrentPageData = () => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return data.slice(startIndex, endIndex);
    };

    const syncScroll = (src, target) => {
        if (src.current && target.current) {
            target.current.scrollLeft = src.current.scrollLeft;
        }
    };

    const handleContentScroll = () => syncScroll(contentRef, scrollRef);
    const handleScrollScroll = () => syncScroll(scrollRef, contentRef);

    useEffect(() => {
        const contentElement = contentRef.current;
        const scrollElement = scrollRef.current;

        if (contentElement && scrollElement) {
            //@ts-ignore
            setContentWidth(contentElement?.scrollWidth);
            //@ts-ignore
            contentElement?.addEventListener("scroll", handleContentScroll);
            //@ts-ignore
            scrollElement?.addEventListener("scroll", handleScrollScroll);

            return () => {
                //@ts-ignore
                contentElement?.removeEventListener("scroll", handleContentScroll);
                //@ts-ignore
                scrollElement?.removeEventListener("scroll", handleScrollScroll);
            };
        }
    }, []);

    const handleSort = (col, direction) => {
        setSortConfig({ key: col.key, direction });
        onSort(col, direction);
    };

    const handleVersionMatch = (record) => {
        if (record["List_Price"]) {
            // Set filters based on selected vehicle
            clearFilters();
            updateFilter("filterMake", newPositiveAttributeFilter(Md.CorrMake, [record.Make]));
            updateFilter("filterModel", newPositiveAttributeFilter(Md.CorrModel, [record.Model]));
            updateFilter("filterTrim", newPositiveAttributeFilter(Md.TrimName, [record.Trim]));
            updateFilter("filterFuel", newPositiveAttributeFilter(Md.Fuel, [record.Energy]));
            // Store selected vehicle data for automatic selection in car selector
            setMatchVehicle(record.Vehicle);

            // Navigate to car selector
            history.push("/vehicles");
        } else {
            message.warning("Please choose a vehicle with a list price.");
        }
    };

    // Helper function to calculate background color with a smooth gradient
    const getLeasingFactorColor = (value) => {
        if (range === 0) return `rgba(0, 0, 0, 0)`; // Avoid division by zero

        const normalized = (value - minLeasingFactor) / range; // 0 to 1

        // Reverse the mapping: 0 = Green (120), 1 = Red (0)
        const hue = (1 - normalized) * 140;

        // Calculate opacity based on normalized value
        const opacity = 0.5 + normalized * 0.5; // 0.3 to 1

        return `hsla(${hue}, 100%, 50%, ${opacity.toFixed(2)})`;
    };

    return (
        <div className="flex flex-col gap-4">
            <div ref={scrollRef} className="overflow-x-auto h-5">
                <div style={{ width: `${contentWidth}px` }} />
            </div>
            <div ref={contentRef} className="max-h-[500px] overflow-x-auto rounded-lg scrollbar-hide shadow-md">
                <table className="table-fixed w-full border-collapse">
                    <colgroup>
                        {columns.map((col) => (
                            <col
                                key={col.key}
                                style={{
                                    minWidth: col.key === "Vehicle" ? "400px" : ["Web_Rental_Includes"].includes(col.key) ? "160px" : "50px",
                                    maxWidth: col.key === "Vehicle" ? "550px" : ["Web_Rental_Includes"].includes(col.key) ? "220px" : col.key === "Leasing_Factor" ? "150px" : "100px",
                                    width: col.key === "Vehicle" ? "300px" : ["Web_Rental_Includes"].includes(col.key) ? "160px" : col.key === "Leasing_Factor" ? "100px" : ["Months", "Mileage", "Channel", "Finance_Type"].includes(col.key) ? "70px" : "80px",
                                }}
                            />
                        ))}
                    </colgroup>
                    <thead className="sticky top-0 z-10 bg-white shadow-sm">
                        <tr className="border-b h-8">
                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    title={col.key}
                                    className={`px-3 py-1 text-xs font-medium text-white bg-indigo-700 transition-colors
                                    ${col.key === stickyColumn ? "sticky left-0 bg-indigo-700 text-white shadow-sm z-10" : ""}
                                    ${onSort && ["Vehicle", "List_Price", "Leasing_Factor", "Adj_Rental"].includes(col.key) ? "cursor-pointer hover:bg-indigo-900" : ""}`}
                                    onClick={() => onSort && ["Vehicle", "List_Price", "Leasing_Factor", "Adj_Rental"].includes(col.key) && handleSort(col, sortConfig.key === col.key && sortConfig.direction === "asc" ? "desc" : "asc")}
                                >
                                    <div className="flex items-center gap-2">
                                        <span title={col.key} className={`${["Finance_Type", "Web_Rental", "Web_Deposit"].includes(col.key) || col.title === "Leasing_Factor" ? "truncate flex-1" : "break-words flex-1"}`}>
                                            {col.title}
                                        </span>
                                        {onSort && ["Vehicle", "List_Price", "Leasing_Factor", "Adj_Rental"].includes(col.key) && (
                                            <div className="flex flex-col">
                                                <CaretUpFill
                                                    width={10}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSort(col, "desc");
                                                    }}
                                                    className={`fill-current cursor-pointer ${sortConfig.key === col.key && sortConfig.direction === "desc" ? "text-white" : "text-gray-300 opacity-70"} hover:text-white`}
                                                />
                                                <CaretDownFill
                                                    width={10}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSort(col, "asc");
                                                    }}
                                                    className={`fill-current cursor-pointer ${sortConfig.key === col.key && sortConfig.direction === "asc" ? "text-white" : "text-gray-300 opacity-70"} hover:text-white`}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-white">
                        {getCurrentPageData().map((row, rowIndex) => (
                            <tr key={rowIndex} className="border-b h-7 hover:bg-gray-50">
                                {columns.map((col) => {
                                    // Check if the current column is 'Leasing_Factor'
                                    if (col.key === "Leasing_Factor") {
                                        const value = parseFloat(row[col.key]);
                                        const backgroundColor = !isNaN(value) ? getLeasingFactorColor(value) : "transparent";
                                        return (
                                            <td
                                                key={col.key}
                                                className={`px-3 py-1 bg-white
                                                ${col.key === stickyColumn ? "sticky left-0 text-left shadow-sm" : "text-center"} 
                                                ${col.className || ""}
                                                text-gray-600 text-[11px]
                                                whitespace-nowrap overflow-hidden text-ellipsis`}
                                                title={row[col.key]}
                                                style={{ backgroundColor }}
                                            >
                                                {row[col.key]}
                                            </td>
                                        );
                                    }

                                    return (
                                        <td
                                            key={col.key}
                                            className={`px-3 py-1 bg-white
                                            ${col.key === stickyColumn ? "sticky left-0 text-left shadow-sm" : "text-center"} 
                                            ${col.className || ""}
                                            text-gray-600 text-[11px]
                                            whitespace-nowrap overflow-hidden text-ellipsis`}
                                            title={row[col.key]}
                                        >
                                            {col.key === "Vehicle" ? (
                                                <div className="flex items-center justify-between">
                                                    <span className="text-ellipsis overflow-hidden">{row[col.key]}</span>
                                                    <button onClick={() => handleVersionMatch(row)} className="hover:opacity-50">
                                                        <MagicWand className="w-4 h-4 text-indigo-900" />
                                                    </button>
                                                </div>
                                            ) : (
                                                row[col.key]
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 rounded-b-lg shadow-sm">
                <div className="flex items-center gap-3">
                    <button onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="p-2 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-white transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m15 18-6-6 6-6" />
                        </svg>
                    </button>

                    <div className="flex items-center gap-1">
                        <span className="text-sm font-medium text-gray-700">{currentPage}</span>
                        <span className="text-gray-500 text-sm">/</span>
                        <span className="text-sm text-gray-500">{totalPages}</span>
                    </div>

                    <button onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="p-2 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-white transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m9 18 6-6-6-6" />
                        </svg>
                    </button>
                </div>

                <div>
                    <p className="text-xs text-gray-500">
                        Showing <span className="font-medium">{(currentPage - 1) * rowsPerPage + 1}</span> to <span className="font-medium">{Math.min(currentPage * rowsPerPage, data.length)}</span> of <span className="font-medium">{data.length}</span> results
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CondensedTable;
